import React, { useState, useEffect, useRef } from 'react';
import styles from './WelcomingBar.module.css';
import YouMayAlsoLike from '../components/YouMayAlsoLike';
import { useData } from '../contexts/DataContext';

const WelcomingBar = ({ activeTab, scrollTarget, onNavigate }) => {
  const { hotelData, loading } = useData();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHoursExpanded, setIsHoursExpanded] = useState(false);
  const [barHours, setBarHours] = useState([]);
  const [events, setEvents] = useState([]);
  const pageRef = useRef(null);
  const whatsOnRef = useRef(null);

  // Process data when hotelData changes
  useEffect(() => {
    if (hotelData) {
      setBarHours(hotelData.openingHours?.bar || []);
      setEvents(hotelData.events?.filter(event => event.active) || []);
    } else {
      // Fallback to default data when no hotelData available
      setBarHours([
        { day: 'Monday', hours: '5:00 PM - 9:30 PM' },
        { day: 'Tuesday', hours: '5:00 PM - 9:30 PM' },
        { day: 'Wednesday', hours: '5:00 PM - 11:00 PM' },
        { day: 'Thursday', hours: '4:00 PM - 11:00 PM' },
        { day: 'Friday', hours: '4:00 PM - 11:00 PM' },
        { day: 'Saturday', hours: '3:00 PM - 11:00 PM' },
        { day: 'Sunday', hours: '3:00 PM - 9:30 PM' }
      ]);
      setEvents([
        {
          id: 1,
          title: 'Quiz Night',
          day: 'Every Wednesday',
          time: '9:00 PM',
          description: 'Test your knowledge at our weekly quiz night. All welcome, small entry fee and prize for the winners!'
        },
        {
          id: 2,
          title: 'Dominoes Night',
          day: 'Every Thursday',
          time: '8:00 PM',
          description: 'Join us for our friendly dominoes night. Newcomers welcome!'
        }
      ]);
    }
  }, [hotelData]);

  // Scroll to appropriate section when component mounts
  useEffect(() => {
    if (scrollTarget === 'whats-on' && whatsOnRef.current) {
      // Scroll to What's On section with padding to clear the navigation bar
      const elementTop = whatsOnRef.current.offsetTop;
      const offset = 100; // Add 100px to clear the navigation bar and provide breathing room
      window.scrollTo({ 
        top: elementTop - offset, 
        behavior: 'smooth' 
      });
    } else if (!scrollTarget) {
      // Scroll to top for normal welcoming-bar route
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [activeTab, scrollTarget]);

  // Bar images
  const barImages = [
    // First 5 images in specified order
    { src: '/website_images/bar_facing_bar.jpg', alt: 'Bar counter view' },
    { src: '/website_images/bar_pool_table.jpg', alt: 'Pool table in bar area' },
    { src: '/website_images/bar_pump_goblin.jpg', alt: 'Beer pump with goblin ale' },
    { src: '/website_images/bar_pump.jpg', alt: 'Beer pump close-up' },
    { src: '/website_images/bar_spirits.jpg', alt: 'Bar spirits selection' },
    { src: '/website_images/bar_taps_tall.jpg', alt: 'Tall view of bar taps' },
    // Remaining images in random order (removed bar_taps and bar_tone1)
    { src: '/website_images/bar_behind_bar.jpg', alt: 'Behind the bar view' },
    { src: '/website_images/bar_hall_lights.jpg', alt: 'Bar hall with lighting' },
    { src: '/website_images/bar_seats1.jpg', alt: 'Bar seating area 1' },
    { src: '/website_images/bar_seats2.jpg', alt: 'Bar seating area 2' },
    { src: '/website_images/bar_seats3.jpg', alt: 'Bar seating area 3' },
    { src: '/website_images/bar_terrace.jpg', alt: 'Bar terrace area' },
    { src: '/website_images/bar_tone2.jpg', alt: 'Bar atmosphere 2' },
    { src: '/website_images/bar_tone3.jpg', alt: 'Bar atmosphere 3' },
    { src: '/website_images/bar_tone4.jpg', alt: 'Bar atmosphere 4' },
    { src: '/website_images/bar_tone5.jpg', alt: 'Bar atmosphere 5' },
    { src: '/website_images/bar_tone6.jpg', alt: 'Bar atmosphere 6' },
    { src: '/website_images/bar_tone7.jpg', alt: 'Bar atmosphere 7' },
    { src: '/website_images/bar_tone8.jpg', alt: 'Bar atmosphere 8' }
  ];


  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === barImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? barImages.length - 1 : prevIndex - 1
    );
  };

  const toggleHours = () => {
    setIsHoursExpanded(!isHoursExpanded);
  };

  return (
    <div ref={pageRef} className={styles['welcoming-bar-page']}>
      <div className={styles.container}>
        <h1 className={styles['page-title']}>Welcoming Bar</h1>
        
        {/* Bar Description */}
        <div className={styles['description-section']}>
          <p>
            Step into our welcoming bar where traditional Welsh hospitality meets a carefully curated selection 
            of drinks. Our bar features local and regional ales, an extensive wine list, and premium spirits 
            - something to suit every taste and occasion.
          </p>
          <p>
            Whether you're popping in for a quick pint after exploring Harlech Castle, settling in for an evening 
            with friends, or enjoying pre-dinner drinks, our comfortable bar area provides the perfect atmosphere 
            for relaxation and conversation.
          </p>
        </div>
        
        {/* Image Carousel */}
        <div className={styles['carousel-container']} data-testid="image-carousel">
          <button className={styles['carousel-btn']} onClick={prevImage} aria-label="Previous image">
            ‹
          </button>
          <div className={styles.carousel}>
            <img 
              src={barImages[currentImageIndex].src}
              alt={barImages[currentImageIndex].alt}
              className={styles['carousel-image']}
            />
          </div>
          <button className={styles['carousel-btn']} onClick={nextImage} aria-label="Next image">
            ›
          </button>
        </div>

        {/* Bar Opening Hours */}
        <div className={styles['opening-hours-section']}>
          <button className={styles['hours-toggle']} onClick={toggleHours}>
            Bar Opening Hours <span className={styles['hours-arrow']}>{isHoursExpanded ? '▲' : '▼'}</span>
          </button>
          <div 
            className={`${styles['hours-content']} ${isHoursExpanded ? styles.expanded : ''}`}
            data-testid="hours-content"
          >
            <div className={styles['hours-grid']}>
              {barHours.map((item, index) => (
                <div key={index} className={styles['hours-day']}>
                  <span className={styles['day-name']}>{item.day}</span>
                  <span className={styles['day-hours']}>{item.hours}</span>
                </div>
              ))}
            </div>
          </div>
        </div>


        {/* What's On Section */}
        <div ref={whatsOnRef} id="whats-on" className={styles['whats-on-section']}>
          <h2 className={styles['whats-on-title']}>What's On</h2>
          {loading ? (
            <div className={styles['loading-message']}>Loading events...</div>
          ) : events.length > 0 ? (
            <div className={styles['events-grid']}>
              {events.map((event) => (
                <div key={event.id} className={styles['event-card']}>
                  <h3 className={styles['event-title']}>{event.title}</h3>
                  <div className={styles['event-timing']}>
                    <span className={styles['event-day']}>{event.day}</span>
                    <span className={styles['event-time']}>{event.time}</span>
                  </div>
                  <p className={styles['event-description']}>{event.description}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles['no-events']}>
              <p>No events currently scheduled. Check back soon!</p>
            </div>
          )}
        </div>
      </div>
      <YouMayAlsoLike currentPage="welcoming-bar" onNavigate={onNavigate} />
    </div>
  );
};

export default WelcomingBar;